import type { ProspectMatterType } from '@common/enums';
import { getIntakeFormTranslations } from '@common/i18n/intakeForm';
import { getLocalizedIntakeFormTitle } from '@common/i18n/matterTypes';
import '@fontsource/dancing-script/600.css';
import { getOrdinalContactLabel, getStaticStepLabel } from '@common/i18n/staticStepLabels';
import type { ApprovalResponse } from '@common/types';
import type { FormLockResponse } from '@common/types/formLocks';
import type { RetainerContactAgreementResponse } from '@common/types/retainerAgreements';
import type { SelectOption, TemplateFormField } from '@common/types/templateFormFields';
import type { TemplateFormLanguage, TemplateFormMetadata } from '@common/types/templateForms';
import type { DraftFormSubmissionRequest, FormSubmissionRequest } from '@common/types/workflows';
import { escapeTableValuePipes } from '@common/utils/intakeTableValue';
import { matchesConditionalValue } from '@common/utils/questionVisibility';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, IconButton, Link, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActivityTracker from '@/components/ActivityTracker';
import ExpandableWrapper from '@/components/FormComponent/ExpandableWrapper';
import FormComponent from '@/components/FormComponent/FormComponent';
import LawFirmLogo from '@/components/LawFirmLogo';
import { BORROWER_INFORMATION, CURRENT_ADDRESS, INITIAL_DETAILS, LENDER_INFORMATION, MAILING_INFO, PROPERTY_DETAILS, PURCHASER_INFORMATION, SELLER_INFORMATION } from '@/constants';
import useIsGoogleTranslated from '@/hooks/useIsGoogleTranslated';
import { LawyerInfo } from '@/pages/EmailResponsePages/ClientResponsePages/ClientResponseContent/ClientResponseContent';
import ClientViewWrapper from '@/pages/QuoteIntakeClientView/ClientViewWrapper';
import {
    clientSideDraftIntakeFormSubmit,
    clientSideFormSubmit,
    clientSideIntakeOnlyFormSubmit,
    clientSideOrchestrationFormSubmit,
    clientSidePostIntakeSubmission,
    clientSideSilentDraftIntakeFormSave,
    extendFormLockStatus,
    getClientPostSubmittedIntakeFormData,
    getClientSideIntakeForms,
    getFormLockStatus,
    getIntakeOnlyFormStatus,
    getPostIntakeFormStatus,
    getRetainerDetailsUsingProspectId,
    postContactIdForLocking,
    unlockFormStatus,
    unlockFormStatusQuickly,
    verifyClientForDraftForm,
} from '@/services/ClientSideTemplateFormService';
import { getIntakeFormStatus } from '@/services/EmailApprovalService';
import { deleteFile } from '@/services/FileService';
import { getIntakePreFillData } from '@/services/ProspectService';
import {
    applyPrefillValue,
    applyTagPrefillToQuestionSchema,
    buildDefaultValuesFromQuote,
    buildPrefillByTag,
    convertToHtml,
    generateGroupFieldsData,
    generateGroupedFieldsData,
    isFormValidForDraftSubmission,
    splitGroupedContactFieldsIntoStore,
    parseIntakePrefillResponse,
    validate,
} from '@/utils';
import { isRetryableClientLoadError } from '@/utils/queryError';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';
import BackButton from '../BackButton';
import ButtonSection from '../ButtonSection';
import ClientErrorPage from '../ClientErrorPage';
import { isBlockingIntakeStatus } from '../constants';
import FormContainer from '../FormContainer';
import IntakeFormStepper from '../IntakeFormStepper';
import { intakeFormContainerStyles, mainWrapperStyles } from '../styles';
import { getEmailsFromWrappedContacts, getLowestGroupsLowestEl, hasDuplicateContactEmails, initScroll, scrollToError, syncDuplicateEmailErrorsInWrappedContacts } from '../utils';
import VerifyEmailDialog from '../VerifyEmailDialog';
import { useSilentDraftAutosave } from './hooks/useSilentDraftAutosave';
import LegalDocumentModal from './LegalDocumentModal';
import RetainerAcceptanceTable from './RetainerAcceptanceTable';
import { ACTIVITY_TRACKER_TIMEOUT_MS, ACTIVITY_TRACKER_WARNING_TIME_MS, FORM_LOCK_EXTENSION_INTERVAL_MS } from './timingConfig';
import { getFormFieldDisplayValue, hasVisibleFields, isEmptyFormValue, isEmptyRequiredFieldValue } from './types';

function applyDuplicateEmailErrorsToSellers(sellersInfoData: TemplateFormField[][], wrapCount: number, duplicateEmailError: string): boolean {
    syncDuplicateEmailErrorsInWrappedContacts(sellersInfoData, wrapCount, duplicateEmailError);
    return !hasDuplicateContactEmails(getEmailsFromWrappedContacts(sellersInfoData, wrapCount));
}

function getRepeatedContactSegment(question: TemplateFormField): string | undefined {
    const value = question.questionSchema.value?.toString();
    return question.questionSchema.type === 'table' && value ? escapeTableValuePipes(value) : value;
}

function getActiveSellersInfoData(sellersStore: TemplateFormField[][], wrapCount: number): TemplateFormField[][] {
    const wrapCountInt = wrapCount ? parseInt(`${wrapCount}`, 10) : 1;
    if (!wrapCountInt) return [];

    return Array(wrapCountInt)
        .fill(null)
        .map((_seller, sellerIndex) => sellersStore[sellerIndex] ?? []);
}

function getMatterBasedStepName(process: ProspectMatterType | undefined) {
    switch (process) {
        case 'Sale':
            return SELLER_INFORMATION;
        case 'Purchase':
            return PURCHASER_INFORMATION;
        case 'Refinance':
            return BORROWER_INFORMATION;
        case 'PrivateLender':
            return LENDER_INFORMATION;
        default:
            return SELLER_INFORMATION;
    }
}

function getMatterBasedMailingInfo(process: ProspectMatterType | undefined) {
    switch (process) {
        case 'Refinance':
            return CURRENT_ADDRESS;
        case 'PrivateLender':
            return CURRENT_ADDRESS;
        case 'Purchase':
        case 'Sale':
            return MAILING_INFO;
        default:
            return MAILING_INFO;
    }
}

// Static Methods
const isGroupWrappable = (groupName: string) => {
    // this function tells us if this particular group is one of those groups
    // where we need to clone the question set based on selected clients
    // can be sellers, purchasers, refinancers etc...
    const groupBucket = [SELLER_INFORMATION, PURCHASER_INFORMATION, BORROWER_INFORMATION, LENDER_INFORMATION];
    return groupBucket.includes(groupName);
};

//Hardcoded values for the questions that need to be disabled for the multi user support
const questionsNameThatNeedToBeDisabled = ['purchasersListedOnPurchaseAndSaleAgreement', 'sellersListedOnPurchaseAndSaleAgreement', 'borrowersList'];
const contactHydrationPrefillFields = ['firstName', 'lastName', 'email'];

export default function IntakeFormView() {
    const wrapperElementId = useId();
    const isGoogleTranslated = useIsGoogleTranslated();
    const theme = useTheme();
    const { intakeTemplateId, lawFirmId } = useParams();
    const [searchParams] = useSearchParams();
    const prospectId = searchParams.get('prospectId');
    const instanceId = searchParams.get('instanceId');
    const templateType = searchParams.get('templateType');
    const quoteTemplateId = searchParams.get('quoteTemplateId');
    const draftFormSubmissionId = searchParams.get('draftFormSubmissionId');
    const prospectVersionId = searchParams.get('prospectVersionId');
    const formSubmissionId = searchParams.get('formSubmissionId');
    const prospectCreatedAs = searchParams.get('prospectCreatedAs');
    const lawyerName = searchParams.get('lawyerName');
    const lawyerEmail = searchParams.get('lawyerEmail');
    const lawyerPhone = searchParams.get('lawyerPhone');
    const [draftSubmissionId, setDraftSubmissionId] = useState<string>(() => draftFormSubmissionId ?? '');
    const effectiveDraftFormSubmissionId = draftSubmissionId || draftFormSubmissionId || undefined;
    const URL_ORIGIN = window.location.origin;
    const [sellersStore, setSellersStore] = useState<TemplateFormField[][]>([]);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [canRetryError, setCanRetryError] = useState(false);
    const [intakeFormState, setIntakeFormState] = useState<{
        process?: ProspectMatterType;
        intakeFormDescription: string;
        textAfterSubmitIntakeForClient: string;
    }>({
        intakeFormDescription: '',
        textAfterSubmitIntakeForClient: '',
    });
    const [templateFormFields, setTemplateFormFields] = useState<TemplateFormField[]>([]);
    const [templateMetadata, setTemplateMetadata] = useState<TemplateFormMetadata | null>(null);
    // Language reported by the status endpoints; available before the template loads
    // so pre-verification UI (verify dialog, expiry screens) is localized too.
    const [statusLanguage, setStatusLanguage] = useState<TemplateFormLanguage | undefined>();
    const language = templateMetadata?.language ?? statusLanguage ?? 'en';
    const t = useMemo(() => getIntakeFormTranslations(language), [language]);
    const [intakeFormLoading, setIntakeFormLoading] = useState(false);

    const [steps, setSteps] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const isFinalStep = useMemo(() => currentStep === steps.length - 1, [steps, currentStep]);
    const isContactsInfoStep = useMemo(() => steps[currentStep] === getMatterBasedStepName(intakeFormState.process), [steps, currentStep, intakeFormState.process]);
    const displaySteps = useMemo(() => steps.map((step) => getStaticStepLabel(step, language)), [steps, language]);

    const [groupedFields, setGroupedFields] = useState<Record<string, TemplateFormField[]>>({});
    const [quoteFormData, setQuoteFormData] = useState<any>(null);
    const [contactResponses, setContactResponses] = useState<any>([]);
    const [intakeFormStatus, setIntakeFormStatus] = useState<ApprovalResponse | undefined>(undefined);
    const [wrapCount, setWrapCount] = useState(1);
    const [currentAccordionIndex, setCurrentAccordionIndex] = useState<number | null>(0);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const contactResponsesCount = contactResponses?.length || 0;

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [clientEmail, setClientEmail] = useState(() => {
        const emailItem = sellersStore[0]?.find((item) => item.name === 'email');
        return emailItem?.questionSchema?.value || ''; // Set '' or any default value
    });
    const [verifyEmail, setVerifyEmail] = useState('');
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [draftData, setDraftData] = useState<any>();
    const [postIntakeData, setPostIntakeData] = useState<any>();
    const [isDraftFormSubmitted, setIsDraftFormSubmitted] = useState<boolean>(false);
    const [isDraftSubmitDisabled, setIsDraftSubmitDisabled] = useState<boolean>(false);
    const [areAllRequiredFieldsFilled, setAreAllRequiredFieldsFilled] = useState(false);
    const [inactiveTimerReached, setInactiveTimerReached] = useState(false);
    const [inactiveSaveFailed, setInactiveSaveFailed] = useState(false);
    const [isModalVerified, setIsModalVerified] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [textLoading, setTextLoading] = useState(true);
    const navigate = useNavigate();
    const [lockInfo, setLockInfo] = useState<FormLockResponse | null>(null);
    const [contactId, setContactId] = useState<string>('');
    const [showLegalDocument, setShowLegalDocument] = useState(false);
    const [retainerResponse, setRetainerResponse] = useState<RetainerContactAgreementResponse[]>([]);

    // Client name used to pre-fill LegalDocumentModal text input
    // TODO: Remove this if we ever want users to type their names for e-sign compliance reasons
    const clientName = useMemo(() => {
        const matchedContact = retainerResponse.find((item) => item.contactId === contactId);
        if (!matchedContact) return undefined;
        return matchedContact.middleName ? `${matchedContact.firstName} ${matchedContact.middleName} ${matchedContact.lastName}` : `${matchedContact.firstName} ${matchedContact.lastName}`;
    }, [retainerResponse, contactId]);
    const resolvedProspectId = prospectId || postIntakeData?.prospectId || lockInfo?.prospectId;

    useEffect(() => {
        const baseSteps = [INITIAL_DETAILS, getMatterBasedStepName(intakeFormState.process), getMatterBasedMailingInfo(intakeFormState.process), PROPERTY_DETAILS];
        const visibleSteps = baseSteps.filter((name) => hasVisibleFields(groupedFields[name]));
        setSteps(visibleSteps);
    }, [groupedFields, intakeFormState.process]);

    // This is to unlock the form when the user close the tab or refreshes the page
    useEffect(() => {
        const handleUnlock = () => {
            if (contactId && resolvedProspectId) {
                unlockFormStatusQuickly({
                    contactId,
                    prospectId: resolvedProspectId,
                });
            }
        };

        if (isMobile) {
            const handleVisibility = () => {
                if (document.visibilityState === 'hidden') {
                    handleUnlock();
                }
            };
            document.addEventListener('visibilitychange', handleVisibility);
            window.addEventListener('pagehide', handleUnlock);

            return () => {
                document.removeEventListener('visibilitychange', handleVisibility);
                window.removeEventListener('pagehide', handleUnlock);
            };
        } else {
            window.addEventListener('beforeunload', handleUnlock);

            return () => {
                window.removeEventListener('beforeunload', handleUnlock);
            };
        }
    }, [isMobile, contactId, resolvedProspectId]);

    useEffect(() => {
        const emailItem = sellersStore[0]?.find((item) => item.name === 'email');
        setClientEmail(emailItem?.questionSchema?.value || '');
    }, [sellersStore, wrapCount]);

    // Enable/Disable Draft Submit button based on required fields in the dynamic groups
    useEffect(() => {
        setIsDraftSubmitDisabled(!isFormValidForDraftSubmission(sellersStore, wrapCount));
    }, [sellersStore, wrapCount]);

    let intakeUrl: string | undefined;

    if (prospectId && (templateType === 'QuoteIntake' || templateType === 'IntakeOnly')) {
        intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${lawyerEmail}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&draftFormSubmissionId=${draftSubmissionId}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`;
    } else if (templateType === 'IntakeOnly') {
        intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?lawyerName=${lawyerName}&lawyerEmail=${lawyerEmail}&lawyerPhone=${lawyerPhone}&draftFormSubmissionId=${draftSubmissionId}&templateType=IntakeOnly`;
    }

    const handleValueChange = async (value: any, groupIndex: number, _fieldIndex: number, templateFormFieldId: string, name: string, fieldObject?: any) => {
        // Handle validation
        if (fieldObject) {
            const isRequired =
                templateType === 'QuoteIntake' ? fieldObject.questionValidationSchema?.required || fieldObject?.isRequiredForFullAutomation : fieldObject.questionValidationSchema?.required;
            const isRequiredAndNotEmpty = isRequired && value;
            const onChangeChecks = fieldObject.questionValidationSchema?.onChange || fieldObject.questionValidationSchema?.onchange;

            // Reset the required state on value set
            // Make sure this doesn't affect any other validations
            if (isRequiredAndNotEmpty) {
                fieldObject.questionSchema.isError = false;
                fieldObject.questionSchema.helper = '';
            }
            // if the field is required and not empty then check for onChange validations and set the helperText and error accordingly
            if (onChangeChecks) {
                Object.keys(onChangeChecks).forEach((check: string) => {
                    const type = check === 'type' ? onChangeChecks[check] : check;
                    const extraParam = check === 'type' ? null : onChangeChecks[check];
                    const validationStatus = validate(type, value, extraParam, language);
                    if (!validationStatus || typeof validationStatus !== 'boolean') {
                        // set the validationStatus on the fieldObjects helperText and add them back to groupedFields
                        fieldObject.questionSchema.helper = validationStatus;
                        fieldObject.questionSchema.isError = true;
                    }

                    // validate seller/purchaser/borrower ID type
                    if (type === 'notEqual' && isContactsInfoStep) {
                        const relatedField = sellersStore[groupIndex]?.find((q) => q.questionSchema.name === extraParam);

                        // If both the fields are select then disable the option from the related field that matches current field value
                        if (relatedField && fieldObject.questionSchema.type === 'select') {
                            const newOptions = relatedField.questionSchema.selectOptions?.map((selectOption: SelectOption) => {
                                if (selectOption.selectItemValue === value) {
                                    return {
                                        ...selectOption,
                                        isSelectItemDisabled: true,
                                    };
                                }
                                return {
                                    ...selectOption,
                                    isSelectItemDisabled: false,
                                };
                            });
                            relatedField.questionSchema.selectOptions = newOptions;
                        }

                        if (relatedField?.questionSchema.value && relatedField?.questionSchema.value === value) {
                            fieldObject.questionSchema.helper = t.formValidation.valueCannotEqual(relatedField?.title ?? '');
                            fieldObject.questionSchema.isError = true;
                            relatedField.questionSchema.helper = t.formValidation.valueCannotEqual(fieldObject?.title ?? '');
                            relatedField.questionSchema.isError = true;
                        } else {
                            fieldObject.questionSchema.helper = '';
                            fieldObject.questionSchema.isError = false;
                            relatedField.questionSchema.helper = '';
                            relatedField.questionSchema.isError = false;
                        }
                    }
                    // set the updated data on store
                    setGroupedFields((prevGroupedFields) => {
                        const updatedGroupedFields = { ...prevGroupedFields };
                        updatedGroupedFields[steps[currentStep]].find((que) => que.templateFormFieldId === templateFormFieldId).questionSchema = fieldObject.questionSchema;
                        return updatedGroupedFields;
                    });
                });
            }
        }
        // Value update on State - logic
        // Common case for all steps except Seller(s) information
        if (currentStep !== 1) {
            setGroupedFields((prevGroupedFields) => {
                const updatedGroupedFields = { ...prevGroupedFields };
                const targetQue = updatedGroupedFields[steps[currentStep]].find((que) => que.templateFormFieldId === templateFormFieldId);
                // targetQue.questionSchema.value = value
                // handle conditional question
                // if the question is a conditional question then set the isVisibleToUser to true/false
                if (targetQue?.questionSchema?.conditions) {
                    updatedGroupedFields[steps[currentStep]].forEach((que) => {
                        if (que.questionSchema.conditionedBy === name) {
                            que.isVisibleToUser = matchesConditionalValue(value, que.questionSchema.conditionValue, targetQue.questionSchema.type);
                        }
                    });
                }
                // set the value on the que
                updatedGroupedFields[steps[currentStep]].forEach((que) => {
                    if (que.templateFormFieldId === templateFormFieldId) {
                        que.questionSchema.value = value;
                    }
                });
                return updatedGroupedFields;
            });
        }
        // Temporary Special case for Seller(s) information
        if (isContactsInfoStep) {
            const deleteApiPromises: Promise<unknown>[] = [];
            // Set the state
            setSellersStore((prevSellersStore) => {
                const updatedSellersStore = [...prevSellersStore];
                // If field name is firstIdType and the value is changed after firstId is uploaded
                // then firstId should be cleared and uploaded files should be deleted
                if (fieldObject.questionSchema.name === 'firstIdType') {
                    const previousFiles = prevSellersStore[groupIndex]?.find((que) => que.questionSchema.name === 'firstId')?.questionSchema?.value;

                    if (previousFiles && typeof previousFiles === 'string') {
                        const previousFileIds = previousFiles
                            .split(',')
                            .map((id) => id.trim())
                            .filter(Boolean);
                        previousFileIds.forEach((id) => {
                            deleteApiPromises.push(deleteFile(id));
                        });

                        updatedSellersStore[groupIndex].find((que) => que.questionSchema.name === 'firstId').questionSchema.value = '';
                    }
                }

                // If field name is secondIdType and the value is changed after secondId is uploaded
                // then secondId should be cleared and uploaded files should be deleted
                if (fieldObject.questionSchema.name === 'secondIdType') {
                    const previousFiles = prevSellersStore[groupIndex]?.find((que) => que.questionSchema.name === 'secondId')?.questionSchema?.value;

                    if (previousFiles && typeof previousFiles === 'string') {
                        const previousFileIds = previousFiles
                            .split(',')
                            .map((id) => id.trim())
                            .filter(Boolean);
                        previousFileIds.forEach((id) => {
                            deleteApiPromises.push(deleteFile(id));
                        });

                        updatedSellersStore[groupIndex].find((que) => que.questionSchema.name === 'secondId').questionSchema.value = '';
                    }
                }

                updatedSellersStore[groupIndex].find((que) => que.templateFormFieldId === templateFormFieldId).questionSchema.value = value;
                // for conditional questions
                // if the question is a conditional question then set the isVisibleToUser to true/false
                updatedSellersStore[groupIndex].forEach((que) => {
                    if (que.questionSchema.conditionedBy === name) {
                        que.isVisibleToUser = matchesConditionalValue(value, que.questionSchema.conditionValue, fieldObject.questionSchema.type);
                    }
                });

                if (name === 'email') {
                    const wrapCountInt = wrapCount ? parseInt(`${wrapCount}`, 10) : 1;
                    syncDuplicateEmailErrorsInWrappedContacts(updatedSellersStore, wrapCountInt);
                }

                // Updating the contact response here to sync with contact api
                if (['firstName', 'middleName', 'lastName', 'primaryPhone', 'email'].includes(name) && contactResponses?.length > 0) {
                    setContactResponses((prevContacts) => {
                        const updatedContacts = [...prevContacts];
                        if (updatedContacts[groupIndex]) {
                            updatedContacts[groupIndex] = {
                                ...updatedContacts[groupIndex],
                                [name]: value,
                            };
                        }
                        return updatedContacts;
                    });
                }

                return updatedSellersStore;
            });

            const deleteResults = await Promise.allSettled(deleteApiPromises);
            deleteResults.forEach((result) => {
                if (result.status === 'rejected') {
                    console.error(result.reason);
                }
            });
        }

        // Determines the no. of sellers info set to be rendered on step 2
        if (name === 'sellersListedOnPurchaseAndSaleAgreement' || name === 'purchasersListedOnPurchaseAndSaleAgreement' || name === 'borrowersList') {
            // Set the no. of wrapRepeats
            setWrapCount(value);
        }
    };

    const updateSellersStore = (prevSellersStore: TemplateFormField[][]) => {
        return prevSellersStore.map((group) =>
            group.map((que) => {
                if (que.questionSchema.conditionedBy) {
                    // Find the field that conditions this one
                    const parentField = group.find((parentQue) => parentQue.questionSchema.name === que.questionSchema.conditionedBy);

                    // Set initial visibility based on parent field's value
                    que.isVisibleToUser = matchesConditionalValue(parentField?.questionSchema.value, que.questionSchema.conditionValue, parentField?.questionSchema.type);
                }
                return { ...que }; // Ensure deep copy to trigger re-render
            }),
        );
    };

    useEffect(() => {
        if (sellersStore && sellersStore.length > 0) {
            setSellersStore((prevSellersStore) => {
                const updatedSellersStore = updateSellersStore(prevSellersStore);
                const isStoreChanged = JSON.stringify(prevSellersStore) !== JSON.stringify(updatedSellersStore);

                return isStoreChanged ? updatedSellersStore : prevSellersStore;
            });
        }
    }, [sellersStore]);

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
            document.getElementById(wrapperElementId)?.scrollIntoView({ behavior: 'instant' });
        }
    };

    const validateSteps = async () => {
        // Validate form
        let isValid = true;

        let hasValidEmail = false;

        const newGroupedFields = { ...groupedFields };

        (newGroupedFields[steps[currentStep]] ?? []).forEach((question) => {
            const isRequired = templateType === 'QuoteIntake' ? question.questionValidationSchema?.required || question?.isRequiredForFullAutomation : question.questionValidationSchema?.required;
            const isRequiredAndEmpty =
                isRequired &&
                (question.questionSchema.type === 'table'
                    ? isEmptyRequiredFieldValue(question.questionSchema.value, question.questionSchema.type, question.questionSchema.columns, question.questionSchema.rowCount)
                    : !question.questionSchema.value);
            const isVisibleToUser = templateType === 'QuoteIntake' ? question.isVisibleToUser || question.isRequiredForFullAutomation : question.isVisibleToUser;

            // At the time of implementation Mandatory questionValidationSchema.required
            // is the main driving force for this update
            if (isRequiredAndEmpty && isVisibleToUser) {
                // Set the error on the component and scroll to it if possible
                question.questionSchema.isError = true;
                question.questionSchema.helper = t.formValidation.fieldRequired;
            } else {
                // Remove/reset the error from the question also
                question.questionSchema.isError = false;
                question.questionSchema.helper = '';
            }

            const onChangeChecks = question.questionValidationSchema?.onChange || question.questionValidationSchema?.onchange;

            if (onChangeChecks) {
                Object.keys(onChangeChecks).forEach((check: string) => {
                    const type = check === 'type' ? onChangeChecks[check] : check;
                    const extraParam = check === 'type' ? null : onChangeChecks[check];

                    const value = question.questionSchema?.value?.trim?.() || '';

                    // First check for required
                    if (isRequiredAndEmpty && isVisibleToUser && value === '') {
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = t.formValidation.fieldRequired;
                        return;
                    }

                    const validationStatus = validate(type, value, extraParam, language);

                    if (!validationStatus || typeof validationStatus !== 'boolean') {
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = validationStatus;
                    }
                });
            }

            // Date Validation
            if (question.questionSchema.type === 'date') {
                try {
                    const selectedDate = DateTime.fromJSDate(new Date(question.questionSchema.value));
                    // Date should be valid
                    // Year should be greater than or equal to 1900
                    if (!selectedDate.isValid) {
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = t.formValidation.invalidDate;
                    } else if (selectedDate.year < 1900) {
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = t.formValidation.invalidDate;
                    } else if (selectedDate.year > 2099) {
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = t.formValidation.invalidDate;
                    }
                    /* check if value changed for date in case of visible */
                    if (!question.questionSchema.value && !isRequired) {
                        question.questionSchema.isError = false;
                        question.questionSchema.helper = '';
                    }
                    if (!question.isVisibleToUser) {
                        question.questionSchema.isError = false;
                        question.questionSchema.helper = '';
                    }
                } catch (error) {
                    Sentry.captureException(error, { tags: { phase: 'validateSteps.date' } });
                    question.questionSchema.isError = true;
                    question.questionSchema.helper = t.formValidation.invalidDate;
                }
            }

            if (
                question.questionSchema.name === 'realEstateAgentEmail' ||
                question.questionSchema.name === 'fireInsuranceEmail' ||
                question.title?.toLowerCase()?.includes('email address') ||
                question.title?.toLowerCase()?.includes('adresse e-mail') //this is to check the email address in French
            ) {
                if (question.questionSchema.isError && question.isVisibleToUser) {
                    hasValidEmail = true;
                }
            }

            if (question.questionSchema.isError && question.isVisibleToUser) {
                isValid = false;
                setIsSubmitDisabled(false);
            }
        });

        setGroupedFields(newGroupedFields);

        if (isValid) {
            if (currentStep < steps.length - 1) {
                setCurrentStep((prev) => prev + 1);
                document.getElementById(wrapperElementId)?.scrollIntoView({ behavior: 'instant' });
            } else if (isFinalStep) {
                await prepareFinalPayload();
            }
        } else {
            // Scroll to the first field with error
            window.setTimeout(() => {
                initScroll(scrollToError());
            }, 500);
            if (hasValidEmail) {
                warningSnackbar(t.formValidation.invalidEmailSnackbar, true);
            } else {
                warningSnackbar(t.formValidation.fillRequiredFields, true);
            }

            return;
        }
    };

    // This useEffect checks if all the required fields are filled to auto submit post intake form for multi user support
    useEffect(() => {
        const EXCLUDED_GROUPS = [SELLER_INFORMATION, PURCHASER_INFORMATION, LENDER_INFORMATION, BORROWER_INFORMATION];

        const requiredFields: { name: string; value: any; isEmpty: boolean }[] = [];
        Object.values(groupedFields).forEach((fields) => {
            fields.forEach((field) => {
                const groupTitle = field?.formGroupTitle || field?.questionSchema?.formGroupTitle;

                if (field?.questionValidationSchema?.required && !EXCLUDED_GROUPS.includes(groupTitle)) {
                    requiredFields.push({
                        name: field?.name,
                        value: field?.questionSchema?.value ?? null,
                        isEmpty:
                            field?.questionSchema?.type === 'table'
                                ? isEmptyRequiredFieldValue(field?.questionSchema?.value, field?.questionSchema?.type, field?.questionSchema?.columns, field?.questionSchema?.rowCount)
                                : !field?.questionSchema?.value || field.questionSchema.value.toString().trim() === '',
                    });
                }
            });
        });

        const sellerRequiredFields = sellersStore.slice(0, wrapCount).map((sellerFields, sellerIndex) => {
            const requiredFields = sellerFields
                .filter((field) => field?.questionValidationSchema?.required)
                .map((field) => ({
                    name: field?.name,
                    value: field?.questionSchema?.value ?? null,
                    isEmpty:
                        field?.questionSchema?.type === 'table'
                            ? isEmptyRequiredFieldValue(field?.questionSchema?.value, field?.questionSchema?.type, field?.questionSchema?.columns, field?.questionSchema?.rowCount)
                            : !field?.questionSchema?.value || field.questionSchema.value.toString().trim() === '',
                }));

            return {
                sellerIndex: sellerIndex + 1,
                fields: requiredFields,
            };
        });

        const isAnyGroupedFieldEmpty = requiredFields.some((field) => !field.value || field.isEmpty);

        const isAnySellerFieldEmpty = sellerRequiredFields.some((seller) => seller.fields.some((field) => !field.value || field.isEmpty));

        setAreAllRequiredFieldsFilled(!isAnyGroupedFieldEmpty && !isAnySellerFieldEmpty);
    }, [groupedFields, sellersStore, wrapCount]);

    const validateSellerInfo = async () => {
        // Validate form
        let isValid = true;

        const wrapCountInt = wrapCount ? parseInt(`${wrapCount}`, 10) : 1;
        const sellersInfoData: TemplateFormField[][] = [];
        if (wrapCountInt) {
            Array(wrapCountInt)
                .fill(null)
                .forEach((_seller, sellerIndex) => {
                    sellersInfoData.push(sellersStore[sellerIndex]);
                });
        }

        // Validation for email field
        let emailIsValid = true;
        sellersInfoData.forEach((sellerInfo) => {
            sellerInfo.forEach((question) => {
                if (
                    question.questionSchema.name === 'email' ||
                    question?.title?.toLowerCase()?.includes('email address') ||
                    question?.title?.toLowerCase()?.includes('adresse e-mail') //validation of email field for French
                ) {
                    // Check if the email field has an error
                    if (question.questionSchema.isError && question.isVisibleToUser) {
                        emailIsValid = false;
                    }
                }
            });
        });

        //Validation for ID types
        let isIdTypeValid = true;
        sellersInfoData.forEach((sellerInfo) => {
            sellerInfo.forEach((question) => {
                if (question.questionSchema.name === 'firstIdType' || question.questionSchema.name === 'secondIdType') {
                    if (question.questionSchema.isError && question.isVisibleToUser && question.questionSchema.value) {
                        isIdTypeValid = false;
                    }
                }
            });
        });

        isValid = isValid && emailIsValid && isIdTypeValid;
        sellersInfoData.forEach((sellerInfo) => {
            sellerInfo.forEach((question) => {
                const isRequired = templateType === 'QuoteIntake' ? question?.questionValidationSchema?.required || question?.isRequiredForFullAutomation : question.questionValidationSchema?.required;
                const isRequiredAndEmpty =
                    isRequired &&
                    (question.questionSchema.type === 'table'
                        ? isEmptyRequiredFieldValue(question.questionSchema.value, question.questionSchema.type, question.questionSchema.columns, question.questionSchema.rowCount)
                        : !question.questionSchema.value || question.questionSchema.value === 0 || question.questionSchema.value === '0');
                const isVisibleToUser = question.isVisibleToUser || question?.isRequiredForFullAutomation;
                const onChangeChecks = question.questionValidationSchema?.onChange || question.questionValidationSchema?.onchange;

                if (isRequiredAndEmpty && isVisibleToUser) {
                    // Set the error on the component and scroll to it if possible
                    question.questionSchema.isError = true;
                    question.questionSchema.helper = t.formValidation.fieldRequired;
                } else {
                    // Remove/reset the error from the question also
                    question.questionSchema.isError = false;
                    question.questionSchema.helper = '';
                }

                // Check for onChange validations
                // Keep onChange validation on next click

                if (onChangeChecks) {
                    Object.keys(onChangeChecks).forEach((check: string) => {
                        const type = check === 'type' ? onChangeChecks[check] : check;
                        const extraParam = check === 'type' ? null : onChangeChecks[check];

                        const value = question.questionSchema?.value?.trim?.() || '';

                        // First check for required
                        if (isRequiredAndEmpty && isVisibleToUser && value === '') {
                            question.questionSchema.isError = true;
                            question.questionSchema.helper = t.formValidation.fieldRequired;
                            return;
                        }

                        const validationStatus = validate(type, value, extraParam, language);

                        if (!validationStatus || typeof validationStatus !== 'boolean') {
                            question.questionSchema.isError = true;
                            question.questionSchema.helper = validationStatus;
                        }
                    });
                }

                // Date Validation
                if (question.questionSchema.type === 'date' && (question.questionSchema.value !== '' || question.questionSchema.value !== undefined)) {
                    try {
                        const selectedDate = DateTime.fromJSDate(new Date(question.questionSchema.value));
                        // Check if the date is valid
                        // Date should be valid
                        // Year should be greater than or equal to 1900
                        if (!selectedDate.isValid) {
                            question.questionSchema.isError = true;
                            question.questionSchema.helper = t.formValidation.invalidDate;
                        } else if (selectedDate.year < 1900) {
                            question.questionSchema.isError = true;
                            question.questionSchema.helper = t.formValidation.invalidDate;
                        } else if (selectedDate.year > 2099) {
                            question.questionSchema.isError = true;
                            question.questionSchema.helper = t.formValidation.invalidDate;
                        } else {
                            question.questionSchema.isError = false;
                            question.questionSchema.helper = '';
                        }
                        /* check if value changed for date in case of visible */
                        if (!question.questionSchema.value && !isRequired) {
                            question.questionSchema.isError = false;
                            question.questionSchema.helper = '';
                        }
                        if (!question.isVisibleToUser) {
                            question.questionSchema.isError = false;
                            question.questionSchema.helper = '';
                        }
                    } catch (error) {
                        Sentry.captureException(error, { tags: { phase: 'validateSellerInfo.date' } });
                        question.questionSchema.isError = true;
                        question.questionSchema.helper = t.formValidation.invalidDate;
                    }
                }

                if (question.questionSchema.isError && question.isVisibleToUser) {
                    isValid = false;
                }
            });
        });

        const hasUniqueEmails = applyDuplicateEmailErrorsToSellers(sellersInfoData, wrapCountInt, t.contactInfo.validation.duplicateEmail);
        if (!hasUniqueEmails) {
            isValid = false;
        }

        setSellersStore((prevSellers) => {
            const newSellers = [...prevSellers];
            sellersInfoData?.forEach((sellerInfo, index) => {
                newSellers[index] = sellerInfo;
            });
            return newSellers;
        });

        if (isValid) {
            if (currentStep < steps.length - 1) {
                setCurrentStep((prev) => prev + 1);
                document.getElementById(wrapperElementId)?.scrollIntoView({ behavior: 'instant' });
            } else if (isFinalStep) {
                await prepareFinalPayload();
            }
        } else {
            // Scroll to the first field with error
            // Add extra case for handling Sellers Info
            // Detect which sellers data is in question to scroll to and open that
            window.setTimeout(() => {
                const el = getLowestGroupsLowestEl();
                const groupIndex = el?.getAttribute('data-formgroup-index');
                // Open the accordion
                if (groupIndex || groupIndex === 0 || groupIndex === '0') {
                    setCurrentAccordionIndex(parseInt(groupIndex, 10));
                }
                // Scroll to the component
                initScroll(el);
            }, 500);

            if (!hasUniqueEmails) {
                warningSnackbar(t.contactInfo.validation.duplicateEmailSnackbar, true);
            } else if (!emailIsValid) {
                warningSnackbar(t.formValidation.invalidEmailSnackbar, true);
            } else if (!isIdTypeValid) {
                warningSnackbar(t.formValidation.differentIdTypes, true);
            } else {
                warningSnackbar(t.formValidation.fillRequiredFields, true);
            }
            return;
        }
    };

    const guardAgainstDuplicateContactEmails = () => {
        const sellersInfoData = getActiveSellersInfoData(sellersStore, wrapCount);
        if (!hasDuplicateContactEmails(getEmailsFromWrappedContacts(sellersStore, wrapCount))) {
            return true;
        }

        applyDuplicateEmailErrorsToSellers(sellersInfoData, wrapCount, t.contactInfo.validation.duplicateEmail);
        setSellersStore((prevSellers) => {
            const newSellers = [...prevSellers];
            sellersInfoData.forEach((sellerInfo, index) => {
                newSellers[index] = sellerInfo;
            });
            return newSellers;
        });

        window.setTimeout(() => {
            const el = getLowestGroupsLowestEl();
            const groupIndex = el?.getAttribute('data-formgroup-index');
            if (groupIndex || groupIndex === 0 || groupIndex === '0') {
                setCurrentAccordionIndex(parseInt(groupIndex, 10));
            }
            initScroll(el);
        }, 500);
        warningSnackbar(t.contactInfo.validation.duplicateEmailSnackbar, true);
        return false;
    };

    const updateEmailField = (payload: DraftFormSubmissionRequest, effectiveClientEmail = clientEmail) => {
        // Step 1: Loop through the templateFormFields
        payload.templateFormFields = payload.templateFormFields.map((field) => {
            // Step 2: Check if the field's ID matches with the templateFormFields and name is "email"
            const templateField = templateFormFields.find((templateField) => field.formFieldId === templateField.templateFormFieldId && templateField.name === 'email');

            // If a matching templateField is found, update its formFieldResponse with clientEmail
            if (templateField) {
                let updatedEmail = effectiveClientEmail; // Start with the email used for this draft save.

                // If wrapCount > 1, add values from sellersStore based on wrapCount - 1
                if (wrapCount > 1) {
                    // Loop from index 1 to wrapCount - 1 in sellersStore
                    for (let i = 1; i < wrapCount; i++) {
                        const emailField = sellersStore[i]?.find((item) => item.name === 'email');
                        const additionalEmail = emailField?.questionSchema?.value || '';

                        // Append '|' and the additional email value if it exists
                        updatedEmail += `|${additionalEmail}`;
                    }
                }

                // Return the field with the updated formFieldResponse
                return { ...field, formFieldResponse: updatedEmail };
            }

            return field; // Keep other fields unchanged
        });

        return payload;
    };

    const getContactFieldValue = (contact: any, fieldName: string) => {
        if (fieldName === 'firstName') return contact?.firstName || '';
        if (fieldName === 'lastName') return contact?.lastName || '';
        if (fieldName === 'email') return contact?.contactEmail || contact?.email || '';
        return '';
    };

    const isBlankContactPrefillSegment = (value?: string | null) => !value || value === 'undefined' || value === 'null';

    /**
     * Merge pipe-delimited contact field values from the form snapshot with live contactResponses.
     * Draft / in-progress intake uses gap-fill (snapshot wins when non-blank) so autosaved answers
     * are preserved. Post-intake uses preferContactValues so operational contact rows win over the
     * completed submission snapshot (e.g. after an admin edits email on the prospect).
     */
    const getContactPrefilledResponse = (fieldName: string, currentValue?: string | null, preferContactValues = false) => {
        if (!contactResponsesCount || !contactHydrationPrefillFields.includes(fieldName)) return currentValue;

        const currentValues = (currentValue || '').split('|');
        const values = Array.from({ length: Math.max(currentValues.length, contactResponsesCount) }, (_, index) => {
            const contactValue = getContactFieldValue(contactResponses[index], fieldName);
            if (preferContactValues && !isBlankContactPrefillSegment(contactValue)) {
                return contactValue;
            }
            if (!isBlankContactPrefillSegment(currentValues[index])) return currentValues[index];
            return contactValue;
        });

        return values.join('|');
    };

    /**
     * Post-intake only: override identity fields from contactResponses after mapping the submission
     * snapshot. Intentionally separate from applyLockedContactCount, which gap-fills for drafts and
     * no-ops when there is only one contact. Do not use for draft reloads.
     */
    const applyPostIntakeContactHydration = (groupedFieldsData: Record<string, TemplateFormField[]>) => {
        if (!formSubmissionId || contactResponsesCount === 0) return groupedFieldsData;

        Object.values(groupedFieldsData).forEach((fields) => {
            fields.forEach((field) => {
                if (contactHydrationPrefillFields.includes(field.questionSchema?.name)) {
                    field.questionSchema.value = getContactPrefilledResponse(field.questionSchema.name, field.questionSchema.value, true);
                }
            });
        });

        return groupedFieldsData;
    };

    const buildDraftPayload = (autosaveDraftFormSubmissionId?: string): DraftFormSubmissionRequest | null => {
        const draftIdForPayload = autosaveDraftFormSubmissionId || effectiveDraftFormSubmissionId;
        const effectiveClientEmail = lockInfo?.lockedByEmail || clientEmail;

        if (!effectiveClientEmail || !intakeTemplateId) return null;

        const payload = {
            clientEmail: effectiveClientEmail,
            submittedBy: effectiveClientEmail,
            templateFormId: intakeTemplateId,
            templateFormFields: [],
            ...(draftIdForPayload && { draftFormSubmissionId: draftIdForPayload }),
            ...(prospectId && { prospectId }),
            ...(instanceId && { instanceId }),
            ...(contactResponses?.length > 1 && {
                contacts: contactResponses,
            }),
        } as DraftFormSubmissionRequest;
        const templateFormFieldsForStep1 = groupedFields[INITIAL_DETAILS] ?? [];
        const templateFormFieldsForStep3 =
            intakeFormState.process === 'Refinance' || intakeFormState.process === 'PrivateLender' ? (groupedFields[CURRENT_ADDRESS] ?? []) : (groupedFields[MAILING_INFO] ?? []);
        const templateFormFieldsForStep4 = groupedFields[PROPERTY_DETAILS] ?? [];
        const templateFormFieldsCombined = [...templateFormFieldsForStep1, ...templateFormFieldsForStep3, ...templateFormFieldsForStep4];
        templateFormFieldsCombined.forEach((question) => {
            if (!question.isVisibleToUser && question.isRequiredForFullAutomation && templateType === 'IntakeOnly') {
                return; //Skip hidden fields for IntakeOnly where isRequiredForFullAutomation is true
            }
            if ((question.isVisibleToUser || question.isRequiredForFullAutomation) && question.questionSchema.type !== 'upload') {
                if (question.questionSchema.type === 'date') {
                    if (['purchaseClosingDate', 'dateTypeQuestionKelly'].includes(question.questionSchema.name)) {
                        let formattedDate: string | null | undefined = question.questionSchema.value;
                        if (question.questionSchema.value) {
                            formattedDate = new Date(question.questionSchema.value).toString();
                        } else {
                            formattedDate = '';
                        }
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: formattedDate,
                        });
                    } else {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: question.questionSchema.value ? new Date(question.questionSchema.value).toString() : null,
                        });
                    }
                } else {
                    payload.templateFormFields.push({
                        formFieldId: question.templateFormFieldId || '',
                        formFieldResponse: question.questionSchema.value?.toString() || null,
                    });
                }
            } else if (question.questionSchema.type === 'upload' && question.isVisibleToUser) {
                if (question.questionSchema.value) {
                    payload.templateFormFields.push({
                        formFieldId: question.templateFormFieldId || '',
                        formFieldResponse: question.questionSchema.value,
                    });
                }
            }
        });

        // Prepare the payload for step 2
        const wrapCountInt = wrapCount ? parseInt(`${wrapCount}`, 10) : 1;
        const sellersInfoData: TemplateFormField[][] = [];
        if (wrapCountInt) {
            Array(wrapCountInt)
                .fill(null)
                .forEach((_seller, sellerIndex) => {
                    sellersInfoData.push(sellersStore[sellerIndex] ?? []);
                });
        }

        for (const sellerInfo of sellersInfoData) {
            for (const question of sellerInfo) {
                if (question.isVisibleToUser && question.questionSchema.type !== 'upload') {
                    if (['dateOfBirth', 'dateOfBirthOfCommon', 'dateOfBirthOfSpouse', 'dateOfSeparationAgr'].includes(question.questionSchema.name)) {
                        const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                        if (fieldToUpdate) {
                            let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                            let inputedDob: string | null | undefined = `${formFieldResponse}|`;
                            if (question.questionSchema.value) {
                                inputedDob += new Date(question.questionSchema.value).toString().slice(4, 15);
                            } else {
                                inputedDob += '';
                            }
                            formFieldResponse = inputedDob;
                            fieldToUpdate.formFieldResponse = formFieldResponse;
                        } else {
                            let inputedDob: string | null | undefined = '';
                            if (question.questionSchema.value) {
                                inputedDob = new Date(question.questionSchema.value).toString().slice(4, 15) || '';
                            }
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: inputedDob,
                            });
                        }
                    } else {
                        // if the question is not a button and and formFieldId is already in the payload
                        // then modify the formFIeldResponse to have multiple values
                        // separated by a pipe "sample1Value|sample2Value"
                        const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                        if (fieldToUpdate) {
                            let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                            formFieldResponse = `${formFieldResponse}|${getRepeatedContactSegment(question)}`;
                            fieldToUpdate.formFieldResponse = formFieldResponse;
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: getRepeatedContactSegment(question) || '',
                            });
                        }
                    }
                } else if (question.questionSchema.type === 'upload') {
                    const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                    if (fieldToUpdate) {
                        let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                        const newfileId: string = question.questionSchema.value ? question.questionSchema?.value : '';
                        formFieldResponse = `${formFieldResponse}|${newfileId}`;
                        fieldToUpdate.formFieldResponse = formFieldResponse;
                    } else {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: question?.questionSchema?.value ? question?.questionSchema?.value || '' : '',
                        });
                    }
                } else if (!question.isVisibleToUser) {
                    //If the question is hidden then
                    // if the question is not a button and and formFieldId is already in the payload
                    // then modify the formFIeldResponse to have multiple values
                    // separated by a pipe "sample1Value|sample2Value"
                    const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                    if (fieldToUpdate) {
                        let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                        formFieldResponse = `${formFieldResponse}|`; //Add a empty response since the field is hidden
                        fieldToUpdate.formFieldResponse = formFieldResponse;
                    } else {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: '', //Add a empty response since the field is hidden
                        });
                    }
                }
                if (!question.isVisibleToUser && question.isRequiredForFullAutomation) {
                    const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                    if (fieldToUpdate) {
                        fieldToUpdate.formFieldResponse = question.questionSchema?.value;
                    } else {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: '', //Add a empty response since the field is hidden
                        });
                    }
                }
            }
        }

        return updateEmailField(payload, effectiveClientEmail);
    };

    const handleDraftSubmit = async () => {
        if (!guardAgainstDuplicateContactEmails()) {
            return;
        }

        if (instanceId && prospectId) {
            try {
                await getIntakeFormStatus(instanceId, prospectId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }
        if (draftFormSubmissionId) {
            try {
                await getIntakeOnlyFormStatus(draftFormSubmissionId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }

        if (loading) return;
        const updatedPayload = buildDraftPayload(effectiveDraftFormSubmissionId);
        if (!updatedPayload) return;

        setIsModalVerified(true);
        setLoading(true);
        try {
            const resp = await clientSideDraftIntakeFormSubmit(updatedPayload);
            setClientEmail('');
            setIsDraftFormSubmitted(true);
            setDraftSubmissionId(resp.data.DraftFormSubmissionId);
            setHasUnsavedChanges(false);
            setIsModalVerified(false);
            setIsSubmitted(true);
            if (contactResponses?.length > 0) {
                unlockIntakeForm();
            }
        } catch (err: any) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const saveDraftForInactivity = async () => {
        const payload = buildDraftPayload(effectiveDraftFormSubmissionId);
        if (!payload) {
            warningSnackbar(t.notifications.autoSaveDraftFailed, true);
            return false;
        }

        setLoading(true);
        try {
            const response = await clientSideSilentDraftIntakeFormSave({
                ...payload,
                shouldUpsertDraft: !isDraftSubmitDisabled,
            });
            if (response.data.DraftFormSubmissionId) {
                setDraftSubmissionId(response.data.DraftFormSubmissionId);
                setHasUnsavedChanges(false);
                return true;
            }
            return false;
        } catch (error: unknown) {
            console.error('Silent draft save on inactivity failed:', error);
            errorSnackbar(t.notifications.autoSaveDraftFailed, true);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleInactivityTimeout = async () => {
        try {
            if (!isDraftFormSubmitted && prospectId && !isDraftSubmitDisabled) {
                await handleDraftSubmit();
                return;
            }

            if (formSubmissionId && prospectVersionId && areAllRequiredFieldsFilled) {
                await prepareFinalPayload();
                setIsSubmitted(true);
                return;
            }

            if (formSubmissionId && prospectVersionId) {
                setInactiveTimerReached(true);
                setContactResponses([]);
                setIsSubmitted(true);
                return;
            }

            const saved = await saveDraftForInactivity();
            if (!saved) {
                setInactiveSaveFailed(true);
                setInactiveTimerReached(true);
                setIsSubmitted(true);
                return;
            }

            setInactiveSaveFailed(false);
            setInactiveTimerReached(true);
            setIsSubmitted(true);
        } finally {
            await unlockIntakeForm();
        }
    };

    const silentDraftAutosaveConditions = {
        noFormSubmissionId: !formSubmissionId,
        verifyDialogClosed: !verifyOpen,
        notSubmitted: !isSubmitted,
        intakeFormActive: !isBlockingIntakeStatus(intakeFormStatus?.Status),
        notLoading: !loading,
        legalDocumentClosed: !showLegalDocument,
        hasGroupedFields: Object.keys(groupedFields).length > 0,
        hasSellersStore: sellersStore.length > 0,
    };
    const isSilentDraftAutosaveEnabled = Object.values(silentDraftAutosaveConditions).every(Boolean);

    useSilentDraftAutosave({
        enabled: isSilentDraftAutosaveEnabled,
        shouldUpsertDraft: !isDraftSubmitDisabled,
        initialDraftFormSubmissionId: effectiveDraftFormSubmissionId,
        buildPayload: buildDraftPayload,
        onDraftFormSubmissionIdChange: setDraftSubmissionId,
        isBlocked: loading,
    });

    const handleLegalDocumentComplete = async () => {
        setShowLegalDocument(false);
        setLoading(true);
        try {
            await fetchRetainerDetails(resolvedProspectId);
            if (formSubmissionId && prospectVersionId) {
                await getPostSubmittedIntakeForm();
            } else if (draftFormSubmissionId) {
                await handleVerifyClientDraft({
                    draftFormSubmissionId: draftFormSubmissionId,
                    clientEmail: verifyEmail,
                });
            } else {
                setVerifyOpen(false);
                await fetchAndSetQuoteFormData(prospectId);
            }
        } catch (err: any) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    //This function is to verify the client for draft form when retainer is available
    const handleVerifyClientDraft = async (payload: any) => {
        try {
            const resp = await verifyClientForDraftForm(payload);
            const draftFormResp = JSON.parse(resp.data.formResponse);
            setContactResponses(resp?.data?.contactResponses);
            setClientEmail(resp.data.clientEmail);
            setDraftData(draftFormResp);
            setVerifyOpen(false);

            if (resp.status === 200) {
                await fetchIntakeTemplateData();
            }
        } catch (err: any) {
            if (err.status === 403) {
                errorSnackbar(t.notifications.draftAlreadySubmitted);
            } else if (err.status === 404 || err.status === 400) {
                errorSnackbar(t.notifications.emailNotRecognized);
            } else {
                errorSnackbar(t.notifications.somethingWentWrong, true);
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    //This function is to verify the client for draft form
    const handleVerifyClientOnRevisit = async (checkForLocking: boolean) => {
        if (instanceId && prospectId) {
            try {
                await getIntakeFormStatus(instanceId, prospectId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }
        if (draftFormSubmissionId) {
            try {
                await getIntakeOnlyFormStatus(draftFormSubmissionId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }

        const payload = {
            draftFormSubmissionId: draftFormSubmissionId,
            clientEmail: verifyEmail,
        };
        const payloadForContactId = {
            ...(prospectId ? { prospectId: prospectId } : {}),
            contactEmail: verifyEmail,
            ...(prospectVersionId ? { prospectVersionId: prospectVersionId } : {}),
        };
        setIsModalVerified(true);
        setLoading(true);
        if (checkForLocking) {
            try {
                const response = await postContactIdForLocking(payloadForContactId);
                setContactId(response.data);
                if (response?.status === 200) {
                    const resp = await getFormLockStatus(response.data, prospectId, prospectVersionId);
                    if ((resp?.data?.lockedByEmail || '').toLowerCase() !== (verifyEmail || '').toLowerCase()) {
                        setContactId('');
                        setLockInfo(null);
                        errorSnackbar(t.notifications.formLockedBy(resp?.data?.lockedByEmail));
                        setLoading(false);
                        return;
                    }
                    setLockInfo(resp?.data);
                    if (resp?.data?.globalRetainerFileId && prospectCreatedAs === 'Manually') {
                        //calling the retainer get to know it's status
                        const retainerResp = await fetchRetainerDetails();
                        if (retainerResp?.data?.find((item) => item.contactId === response.data && item?.isRetainerSigned)) {
                            await handleVerifyClientDraft(payload);
                            return;
                        } else {
                            setShowLegalDocument(true);
                            setVerifyOpen(false);
                            setLoading(false);
                            return;
                        }
                    }
                }
            } catch (error: any) {
                console.error(error);
                setLoading(false);
            }
        }
        await handleVerifyClientDraft(payload);
    };

    const fetchRetainerDetails = async (forProspectId?: string | null) => {
        const id = forProspectId || resolvedProspectId;
        if (id) {
            try {
                const retainerResp = await getRetainerDetailsUsingProspectId(id);
                setRetainerResponse(retainerResp?.data);
                return retainerResp;
            } catch (error) {
                console.error(error);
            }
        }
    };

    //This function is used to lock the form
    const getIntakeFormLockStatus = async (contactId: string) => {
        try {
            const resp = await getFormLockStatus(contactId, prospectId, prospectVersionId);
            if ((resp?.data?.lockedByEmail || '').toLowerCase() !== (verifyEmail || '').toLowerCase()) {
                setContactId('');
                setLockInfo(null);
                errorSnackbar(t.notifications.formLockedBy(resp?.data?.lockedByEmail));
                return;
            }
            setLockInfo(resp?.data);
            if (resp?.data?.globalRetainerFileId && prospectCreatedAs === 'Manually') {
                //calling the retainer get to know it's status
                const retainerResp = await fetchRetainerDetails();
                if (retainerResp?.data?.find((item) => item?.contactId === contactId && item?.isRetainerSigned)) {
                    setVerifyOpen(false);
                    await fetchAndSetQuoteFormData(prospectId);
                    return;
                } else {
                    setShowLegalDocument(true);
                    setVerifyOpen(false);
                    return;
                }
            } else {
                setVerifyOpen(false);
                await fetchAndSetQuoteFormData(prospectId);
            }
        } catch (err: any) {
            setLoading(false);
            console.error(err);
        }
    };

    //API call to get the contact ID which belongs to the prospect
    const getContactIdForIntakeForm = async () => {
        if (instanceId && prospectId) {
            try {
                const intakeStatusResp = await getIntakeFormStatus(instanceId, prospectId);
                if (intakeStatusResp?.data?.DraftFormSubmissionId) {
                    const intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${lawyerEmail}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&draftFormSubmissionId=${intakeStatusResp?.data?.DraftFormSubmissionId}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`;
                    navigate(intakeUrl);
                    return;
                } else if (isBlockingIntakeStatus(intakeStatusResp.data.Status)) {
                    setIntakeFormStatus(intakeStatusResp.data);
                    setTextLoading(false);
                }
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setTextLoading(false);
                setVerifyOpen(false);
                return;
            }
        }

        if (draftFormSubmissionId) {
            try {
                await getIntakeOnlyFormStatus(draftFormSubmissionId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }

        setIsModalVerified(true);
        if (!verifyEmail) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const payload = {
            ...(prospectId ? { prospectId: prospectId } : {}),
            contactEmail: verifyEmail,
            ...(prospectVersionId ? { prospectVersionId: prospectVersionId } : {}),
        };
        try {
            const resp = await postContactIdForLocking(payload);
            setContactId(resp.data);
            if (resp?.status === 200) {
                await getIntakeFormLockStatus(resp?.data);
            }
        } catch (err: any) {
            setLoading(false);
            // Not found means wrong email used to enter form
            if (err.status === 404) {
                errorSnackbar(t.notifications.emailNotRecognized);
            }
        } finally {
            setLoading(false);
            setIsModalVerified(false);
        }
    };

    //Function to Unlock the locked status
    const unlockIntakeForm = async () => {
        const payload = {
            prospectId: resolvedProspectId,
            contactId: contactId,
        };
        try {
            await unlockFormStatus(payload);
        } catch (err: any) {
            console.error(err);
        }
    };

    //This function to get the post submitted intake form
    const getPostSubmittedIntakeForm = async () => {
        if (formSubmissionId) {
            try {
                await getPostIntakeFormStatus(formSubmissionId);
            } catch (error: any) {
                if (error?.response?.data?.Status === 'Forbidden') {
                    setVerifyOpen(false);
                    setIntakeFormStatus({
                        Status: error?.response?.data?.Status || 'Failed',
                        Message: error?.response?.data?.Message,
                    });
                }
                return;
            }
        }

        setIsModalVerified(true);
        if (!verifyEmail) {
            return;
        }

        const payload = {
            prospectVersionId: prospectVersionId,
            formSubmissionId: formSubmissionId,
            clientEmail: verifyEmail,
        };
        const payloadForContactId = {
            ...(prospectId ? { prospectId: prospectId } : {}),
            contactEmail: verifyEmail,
            ...(prospectVersionId ? { prospectVersionId: prospectVersionId } : {}),
        };
        setLoading(true);
        try {
            const response = await postContactIdForLocking(payloadForContactId);
            setContactId(response.data);
            if (response?.status === 200) {
                const resp = await getFormLockStatus(response.data, prospectId, prospectVersionId);
                if ((resp?.data?.lockedByEmail || '').toLowerCase() !== (verifyEmail || '').toLowerCase()) {
                    setContactId('');
                    setLockInfo(null);
                    setLoading(false);
                    errorSnackbar(t.notifications.formLockedBy(resp?.data?.lockedByEmail));
                    return;
                }
                setLockInfo(resp?.data);
                if (resp?.data?.globalRetainerFileId) {
                    const retainerResp = await fetchRetainerDetails(resp.data.prospectId);
                    const isSigned = retainerResp?.data?.find((item) => item.contactId === response.data && item?.isRetainerSigned);
                    if (!isSigned) {
                        setShowLegalDocument(true);
                        setVerifyOpen(false);
                        setLoading(false);
                        setIsModalVerified(false);
                        return;
                    }
                }
            }
        } catch (err: any) {
            setLoading(false);
            console.error('HERE', err);
            if (err.status === 404) {
                errorSnackbar(t.notifications.emailNotRecognized);
            }
        }
        try {
            const resp = await getClientPostSubmittedIntakeFormData(payload);
            setContactResponses(resp?.data?.contactResponses);
            setPostIntakeData(resp.data);
            setVerifyOpen(false);
            if (resp.status === 200) {
                fetchIntakeTemplateData();
            }
        } catch (err: any) {
            if (err.status === 403) {
                errorSnackbar(t.notifications.intakeAlreadySubmitted);
            }
            console.log(err);
        } finally {
            setLoading(false);
            setIsModalVerified(false);
        }
    };

    const mappedPostIntakeData = postIntakeData?.formSubmissionFields
        ?.filter((item) => item?.formFieldResponse !== null && item.formFieldResponse !== '')
        ?.map((item) => ({
            formFieldId: item.formFieldId,
            formFieldResponse: item.formFieldResponse,
        }));

    // Filter and map fields that have non-null and non-empty responses
    const mappedData = draftData
        ?.filter((item) => item.FormFieldResponse !== null && item.FormFieldResponse !== '')
        ?.map((item) => ({
            FormFieldId: item.FormFieldId,
            FormFieldResponse: item.FormFieldResponse,
        }));

    const prepareFinalPayload = async () => {
        if (!guardAgainstDuplicateContactEmails()) {
            setIsSubmitDisabled(false);
            return;
        }

        if (instanceId && prospectId) {
            try {
                await getIntakeFormStatus(instanceId, prospectId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                return;
            }
        }
        if (formSubmissionId) {
            try {
                await getPostIntakeFormStatus(formSubmissionId);
            } catch (error: any) {
                if (error?.response?.data?.Status === 'Forbidden') {
                    setIntakeFormStatus({
                        Status: error?.response?.data?.Status || 'Failed',
                        Message: error?.response?.data?.Message,
                    });
                }
                return;
            }
        }

        if (draftFormSubmissionId) {
            try {
                await getIntakeOnlyFormStatus(draftFormSubmissionId);
            } catch (error: any) {
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setVerifyOpen(false);
                return;
            }
        }

        let payload: FormSubmissionRequest;
        try {
            // Prepare the payload for each step
            payload = {
                submittedBy: lockInfo?.lockedByEmail || clientEmail,
                templateFormId: intakeTemplateId,
                templateFormFields: [],
                ...(effectiveDraftFormSubmissionId && { draftFormSubmissionId: effectiveDraftFormSubmissionId }),
                ...(formSubmissionId && { formSubmissionId }),
                ...(prospectVersionId && { prospectVersionId }),
                ...(prospectId && { prospectId }),
                ...(instanceId && { instanceId }),
                ...(contactResponses?.length > 1 && {
                    contacts: contactResponses,
                }),
                ...(postIntakeData?.prospectId && {
                    prospectId: postIntakeData?.prospectId,
                }),
            } as FormSubmissionRequest;
            const templateFormFieldsForStep1 = groupedFields[INITIAL_DETAILS] ?? [];
            const templateFormFieldsForStep3 =
                (intakeFormState.process === 'Refinance' || intakeFormState.process === 'PrivateLender' ? groupedFields[CURRENT_ADDRESS] : groupedFields[MAILING_INFO]) ?? [];
            const templateFormFieldsForStep4 = groupedFields[PROPERTY_DETAILS] ?? [];
            const templateFormFieldsCombined = [...templateFormFieldsForStep1, ...templateFormFieldsForStep3, ...templateFormFieldsForStep4];
            templateFormFieldsCombined.forEach((question) => {
                if (!question.isVisibleToUser && question.isRequiredForFullAutomation && templateType === 'IntakeOnly') {
                    return; //Skip hidden fields for IntakeOnly where isRequiredForFullAutomation is true
                }
                if (
                    (question.isVisibleToUser || question.isRequiredForFullAutomation || ['purchasePrice', 'salePrice', 'totalLoanAmount'].includes(question.questionSchema.name)) &&
                    question.questionSchema.type !== 'upload'
                ) {
                    if (['purchasePrice', 'salePrice', 'totalLoanAmount'].includes(question.questionSchema.name) && templateType === 'QuoteIntake') {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: question.questionSchema.value?.toString() || '0.00',
                        });
                    }

                    if (question.questionSchema.type === 'date') {
                        if (['purchaseClosingDate', 'saleClosingDate', 'dateTypeQuestionKelly'].includes(question.questionSchema.name)) {
                            let formattedDate: string | null | undefined = question.questionSchema.value;
                            if (question.questionSchema.value) {
                                formattedDate = new Date(question.questionSchema.value).toString();
                            } else {
                                formattedDate = '';
                            }
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: formattedDate,
                            });
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: question.questionSchema.value ? new Date(question.questionSchema.value).toString() : '',
                            });
                        }
                    } else if (question.questionSchema.type === 'currency' && question.isVisibleToUser) {
                        if (!['purchasePrice', 'salePrice', 'totalLoanAmount'].includes(question.questionSchema.name)) {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: question.questionSchema.value ? `$${question.questionSchema.value.toString()}` : '',
                            });
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: question.questionSchema.value?.toString() || null,
                            });
                        }
                    } else {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: question.questionSchema.value?.toString() || null,
                        });
                    }
                } else if (question.questionSchema.type === 'upload' && question.isVisibleToUser) {
                    if (question.questionSchema.value) {
                        payload.templateFormFields.push({
                            formFieldId: question.templateFormFieldId || '',
                            formFieldResponse: question.questionSchema.value,
                        });
                    }
                }
            });

            // Prepare the payload for step 2
            const wrapCountInt = wrapCount ? parseInt(`${wrapCount}`, 10) : 1;
            const sellersInfoData: TemplateFormField[][] = [];
            if (wrapCountInt) {
                Array(wrapCountInt)
                    .fill(null)
                    .forEach((_seller, sellerIndex) => {
                        const sellerInfo = (sellersStore[sellerIndex] ?? []).filter((question) => !(wrapCountInt === 1 && question.questionSchema.isConditionalQuestion && !question.isVisibleToUser));
                        sellersInfoData.push(sellerInfo);
                    });
            }

            for (const sellerInfo of sellersInfoData) {
                for (const question of sellerInfo) {
                    if (question.isVisibleToUser && question.questionSchema.type !== 'upload') {
                        if (question.questionSchema.type === 'date' && !['dateOfBirth', 'dateOfBirthOfCommon', 'dateOfBirthOfSpouse'].includes(question.questionSchema.name)) {
                            if (question.questionSchema.value) {
                                const formattedDate = new Date(question.questionSchema.value).toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                });

                                const existingField = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);

                                if (existingField) {
                                    existingField.formFieldResponse += `|${formattedDate}`;
                                } else {
                                    payload.templateFormFields.push({
                                        formFieldId: question.templateFormFieldId || '',
                                        formFieldResponse: formattedDate,
                                    });
                                }
                            } else {
                                const existingField = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);

                                if (existingField) {
                                    existingField.formFieldResponse += '|';
                                } else {
                                    payload.templateFormFields.push({
                                        formFieldId: question.templateFormFieldId || '',
                                        formFieldResponse: '',
                                    });
                                }
                            }
                        } else if (['dateOfBirth', 'dateOfBirthOfCommon', 'dateOfBirthOfSpouse'].includes(question.questionSchema.name)) {
                            const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                            if (fieldToUpdate) {
                                let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                                let inputedDob: string | null | undefined = `${formFieldResponse}|`;
                                if (question.questionSchema.value) {
                                    inputedDob += new Date(question.questionSchema.value).toString().slice(4, 15);
                                } else {
                                    inputedDob += '';
                                }
                                formFieldResponse = inputedDob;
                                fieldToUpdate.formFieldResponse = formFieldResponse;
                            } else {
                                let inputedDob: string | null | undefined = '';
                                if (question.questionSchema.value) {
                                    inputedDob = new Date(question.questionSchema.value).toString().slice(4, 15) || '';
                                }
                                payload.templateFormFields.push({
                                    formFieldId: question.templateFormFieldId || '',
                                    formFieldResponse: inputedDob,
                                });
                            }
                        } else {
                            // if the question is not a button and and formFieldId is already in the payload
                            // then modify the formFIeldResponse to have multiple values
                            // separated by a pipe "sample1Value|sample2Value"
                            const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                            if (fieldToUpdate) {
                                let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                                formFieldResponse = `${formFieldResponse}|${getRepeatedContactSegment(question)}`;
                                fieldToUpdate.formFieldResponse = formFieldResponse;
                            } else {
                                payload.templateFormFields.push({
                                    formFieldId: question.templateFormFieldId || '',
                                    formFieldResponse: getRepeatedContactSegment(question) || '',
                                });
                            }
                        }
                    } else if (question.questionSchema.type === 'upload') {
                        const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                        if (fieldToUpdate) {
                            let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                            const newfileId: string = question.questionSchema.value ? question.questionSchema?.value : '';
                            formFieldResponse = `${formFieldResponse}|${newfileId}`;
                            fieldToUpdate.formFieldResponse = formFieldResponse;
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: question?.questionSchema?.value ? question?.questionSchema?.value || '' : '',
                            });
                        }
                    } else if (!question.isVisibleToUser) {
                        //If the question is hidden then
                        // if the question is not a button and and formFieldId is already in the payload
                        // then modify the formFIeldResponse to have multiple values
                        // separated by a pipe "sample1Value|sample2Value"
                        const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                        if (fieldToUpdate) {
                            let formFieldResponse = payload?.templateFormFields?.find((field) => field?.formFieldId === question?.templateFormFieldId)?.formFieldResponse;
                            formFieldResponse = `${formFieldResponse}|`; //Add a empty response since the field is hidden
                            fieldToUpdate.formFieldResponse = formFieldResponse;
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: '', //Add a empty response since the field is hidden
                            });
                        }
                    }
                    if (!question.isVisibleToUser && question.isRequiredForFullAutomation) {
                        const fieldToUpdate = payload.templateFormFields.find((field) => field.formFieldId === question.templateFormFieldId);
                        if (fieldToUpdate) {
                            fieldToUpdate.formFieldResponse = question.questionSchema?.value;
                        } else {
                            payload.templateFormFields.push({
                                formFieldId: question.templateFormFieldId || '',
                                formFieldResponse: '', //Add a empty response since the field is hidden
                            });
                        }
                    }
                }
            }
        } catch (error) {
            setIsSubmitDisabled(false);
            Sentry.captureException(error, { tags: { phase: 'prepareFinalPayload.build' } });
            errorSnackbar(t.notifications.somethingWentWrong, true);
            return;
        }

        Sentry.addBreadcrumb({
            category: 'intakeForm',
            message: 'submit',
            data: { fieldCount: payload.templateFormFields.length, wrapCount },
        });

        try {
            if (prospectId && instanceId) {
                await clientSideOrchestrationFormSubmit(instanceId || '', prospectId, payload);
            } else if (templateType && formSubmissionId && prospectVersionId) {
                await clientSidePostIntakeSubmission(payload);
            } else if (templateType && templateType === 'IntakeOnly') {
                await clientSideIntakeOnlyFormSubmit(payload);
            } else {
                await clientSideFormSubmit(payload);
            }
            // show the success message
            if (isFinalStep) {
                setIsSubmitted(true);
            }
            successSnackbar(t.notifications.formSubmitted, true);
            setHasUnsavedChanges(false);
            // Release once the client is done. A failed submit keeps the lock.
            if (contactResponses?.length > 0) {
                await unlockIntakeForm();
            }
            return true;
        } catch (error: any) {
            setIsSubmitDisabled(false);
            Sentry.captureException(error, { tags: { phase: 'prepareFinalPayload.submit' } });

            const responseData = error?.response?.data;
            if (typeof responseData === 'object' && responseData !== null) {
                setIntakeFormStatus({
                    Status: responseData?.Status || 'Failed',
                    Message: responseData?.Message || t.notifications.somethingWentWrong,
                });
                return;
            }
            if (error.status === 403) {
                errorSnackbar(t.notifications.draftAlreadySubmitted);
            } else {
                errorSnackbar(t.notifications.somethingWentWrong, true);
            }
        }
    };
    const fetchIntakeTemplateData = async () => {
        if (!intakeTemplateId) return;

        setIntakeFormLoading(true);
        try {
            const intakeFormTemplateResponse = await getClientSideIntakeForms(intakeTemplateId, quoteTemplateId);
            const { templateFormFields: fields, footerTemplate, headerDescription, process, metadata } = intakeFormTemplateResponse.data;

            setIntakeFormState({
                process,
                intakeFormDescription: headerDescription,
                textAfterSubmitIntakeForClient: footerTemplate,
            });
            setTemplateFormFields([...fields]);
            setTemplateMetadata(metadata ?? null);
        } catch (fetchError: any) {
            const message = typeof fetchError?.response?.data === 'string' ? fetchError.response.data : t.notifications.somethingWentWrong;
            setError(message);
            setCanRetryError(isRetryableClientLoadError(fetchError));
            console.error(fetchError);
            throw fetchError;
        } finally {
            setIntakeFormLoading(false);
        }
    };

    const handleRetryTemplateLoad = () => {
        setError(null);
        setCanRetryError(false);
        void fetchIntakeTemplateData();
    };

    const handleSubmit = async () => {
        try {
            if (isContactsInfoStep) {
                await validateSellerInfo();
            } else {
                await validateSteps();
            }
        } catch (error) {
            setIsSubmitDisabled(false);
            Sentry.captureException(error, { tags: { phase: 'handleSubmit' } });
            errorSnackbar(t.notifications.somethingWentWrongTryAgain, true);
        }
    };

    const getIntakeStatus = async () => {
        if (!draftFormSubmissionId) return;
        setLoading(true);
        try {
            const statusResp = await getIntakeOnlyFormStatus(draftFormSubmissionId);
            if (statusResp?.data?.Language) setStatusLanguage(statusResp.data.Language);
            await fetchQuoteFormData();
            setLoading(false);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.Language) setStatusLanguage(error.response.data.Language);
            await fetchIntakeTemplateData();
            setIntakeFormStatus({
                Status: error?.response?.data?.Status || 'Failed',
                Message: error?.response?.data?.Message,
            });
            setLoading(false);
            setVerifyOpen(false);
            setTextLoading(false);
        }
    };
    const getPostIntakeStatus = async () => {
        if (!formSubmissionId) return;
        setLoading(true);
        try {
            const statusResp = await getPostIntakeFormStatus(formSubmissionId);
            if (statusResp?.data?.Language) setStatusLanguage(statusResp.data.Language);
            fetchQuoteFormData();
            setLoading(false);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.Language) setStatusLanguage(error.response.data.Language);
            fetchIntakeTemplateData();
            setIntakeFormStatus({
                Status: error?.response?.data?.Status || 'Failed',
                Message: error?.response?.data?.Message,
            });
            setTextLoading(false);
            setLoading(false);
        }
    };

    const generatePrefilledGroupFieldsData = () => {
        const defaultValues = buildDefaultValuesFromQuote(quoteFormData || []);
        const prefillByTag = buildPrefillByTag(defaultValues);

        // Track which parent questions have a prefilled value
        const parentPrefilled = {};

        const templateFormFieldsData = templateFormFields.map((field) => {
            const item = { ...field };
            const questionSchema = item?.questionSchema;

            if (contactResponsesCount > 0 && questionsNameThatNeedToBeDisabled.includes(questionSchema.name)) {
                questionSchema.value = `${contactResponsesCount}`;
                setWrapCount(contactResponsesCount);
                parentPrefilled[questionSchema.name] = questionSchema.value;
            } else {
                const keyName = Object.keys(defaultValues).find((k) => questionSchema.name === k);
                if (keyName) {
                    applyPrefillValue(questionSchema, defaultValues);
                }
                applyTagPrefillToQuestionSchema(questionSchema, prefillByTag);
                if (questionsNameThatNeedToBeDisabled.includes(questionSchema.name) && questionSchema.value) {
                    parentPrefilled[questionSchema.name] = questionSchema.value;
                }
            }

            item.questionSchema = questionSchema;
            return item;
        });

        // Show child questions if their parent is prefilled
        templateFormFieldsData.forEach((field) => {
            const questionSchema = field.questionSchema;
            if (questionSchema.isConditionalQuestion) {
                // If parent is in questionsNameThatNeedToBeDisabled and has a prefilled value
                if (questionsNameThatNeedToBeDisabled.includes(questionSchema.conditionedBy) && parentPrefilled[questionSchema.conditionedBy] !== undefined) {
                    // Show child if parent value matches conditionValue
                    field.isVisibleToUser = parentPrefilled[questionSchema.conditionedBy] === questionSchema.conditionValue;
                } else {
                    // Fallback to original logic
                    const sourceQue = templateFormFieldsData.find((source) => {
                        const sourceQuestionSchema = source.questionSchema;
                        return sourceQuestionSchema.name === questionSchema.conditionedBy;
                    });
                    if (sourceQue) {
                        const sourceQueSchema = sourceQue?.questionSchema;
                        field.isVisibleToUser = matchesConditionalValue(sourceQueSchema.value, questionSchema.conditionValue, sourceQueSchema.type) && sourceQue.isVisibleToUser;
                    }
                }
            }
        });

        return generateGroupFieldsData(templateFormFieldsData);
    };

    //Function to generate group data for draft form.

    const generateDraftGroupFieldsData = () =>
        generateGroupedFieldsData({
            mappedData: mappedData,
            templateFormFields: templateFormFields,
            setWrapCount: setWrapCount,
            keys: {
                formFieldId: 'FormFieldId',
                formFieldResponse: 'FormFieldResponse',
            },
            clientEmail: clientEmail,
        });

    //Function to generate group data for post intake .
    const generatePostIntakeGroupFieldsData = () =>
        generateGroupedFieldsData({
            mappedData: mappedPostIntakeData,
            templateFormFields: templateFormFields,
            setWrapCount: setWrapCount,
            keys: {
                formFieldId: 'formFieldId',
                formFieldResponse: 'formFieldResponse',
            },
        });

    const applyLockedContactCount = (groupedFieldsData: Record<string, TemplateFormField[]>) => {
        if (contactResponsesCount <= 1) return groupedFieldsData;

        const contactCountValue = `${contactResponsesCount}`;
        Object.values(groupedFieldsData).forEach((fields) => {
            fields.forEach((field) => {
                if (questionsNameThatNeedToBeDisabled.includes(field.questionSchema?.name)) {
                    field.questionSchema.value = contactCountValue;
                }

                if (contactHydrationPrefillFields.includes(field.questionSchema?.name)) {
                    field.questionSchema.value = getContactPrefilledResponse(field.questionSchema.name, field.questionSchema.value);
                }

                if (field.questionSchema?.isConditionalQuestion && questionsNameThatNeedToBeDisabled.includes(field.questionSchema.conditionedBy)) {
                    field.isVisibleToUser = contactCountValue === field.questionSchema.conditionValue;
                }
            });
        });

        setWrapCount(contactResponsesCount);
        return groupedFieldsData;
    };

    const fetchIntakeFormStatus = async () => {
        if (instanceId && prospectId) {
            setLoading(true);
            try {
                const intakeStatusResp = await getIntakeFormStatus(instanceId, prospectId);
                if (intakeStatusResp?.data?.Language) setStatusLanguage(intakeStatusResp.data.Language);
                if (intakeStatusResp?.data?.DraftFormSubmissionId) {
                    const intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${lawyerEmail}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&draftFormSubmissionId=${intakeStatusResp?.data?.DraftFormSubmissionId}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`;
                    navigate(intakeUrl);
                    return;
                } else if (isBlockingIntakeStatus(intakeStatusResp.data.Status)) {
                    setIntakeFormStatus(intakeStatusResp.data);
                    setTextLoading(false);
                }
                setLoading(false);
                return intakeStatusResp?.data;
            } catch (error: any) {
                console.error('fetchIntakeFormStatus', error);
                if (error?.response?.data?.Language) setStatusLanguage(error.response.data.Language);
                const errObj = {
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                };
                setIntakeFormStatus({
                    Status: error?.response?.data?.Status || 'Failed',
                    Message: error?.response?.data?.Message,
                });
                setTextLoading(false);
                return errObj;
            } finally {
                setLoading(false);
            }
        }
    };

    const fetchAndSetQuoteFormData = async (prospectId: string) => {
        try {
            const quoteFormResponse = await getIntakePreFillData(prospectId);
            setContactResponses(quoteFormResponse?.data?.contactResponses);

            const combinedFormData = parseIntakePrefillResponse(quoteFormResponse?.data);

            setQuoteFormData(combinedFormData);
            await fetchIntakeTemplateData();
        } catch (error) {
            console.error(error);
        }
    };

    const fetchQuoteFormData = async (df?: any) => {
        try {
            if ((prospectId && df) || (prospectId && instanceId) || draftFormSubmissionId || (formSubmissionId && prospectVersionId)) {
                setVerifyOpen(true);
            } else {
                await fetchIntakeTemplateData();
            }
        } catch (error) {
            console.error(error);
        }
    };

    // This function can be called to attempt a navigation
    // This effect sets up the beforeunload event listener
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set for the prompt to appear
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up the event listener
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Call this function in your form's onChange handler
    const handleFormDataChange = useCallback(() => {
        setHasUnsavedChanges(true);
    }, []);

    useEffect(() => {
        const run = async () => {
            if (instanceId && prospectId) {
                fetchIntakeFormStatus().then((d) => {
                    if (isBlockingIntakeStatus(d?.Status)) return;
                    fetchQuoteFormData(d);
                });
            } else if (draftFormSubmissionId) {
                getIntakeStatus().catch(() => {
                    fetchQuoteFormData();
                });
            } else if (formSubmissionId) {
                getPostIntakeStatus().catch(() => {
                    fetchQuoteFormData();
                });
            } else {
                fetchQuoteFormData();
            }
        };
        run();
    }, [instanceId, prospectId, draftFormSubmissionId, formSubmissionId]);

    useEffect(() => {
        if (error && !error.includes('expired')) {
            errorSnackbar(error, true);
        }
    }, [error]);

    const handleBaseData = (groupedFieldsData: Record<string, TemplateFormField[]>, process?: ProspectMatterType) => {
        let baseData: TemplateFormField[];
        switch (process) {
            case 'Sale':
                baseData = [...groupedFieldsData[SELLER_INFORMATION]];
                break;
            case 'Purchase':
                baseData = [...groupedFieldsData[PURCHASER_INFORMATION]];
                break;
            case 'Refinance':
                baseData = [...groupedFieldsData[BORROWER_INFORMATION]];
                break;
            case 'PrivateLender':
                baseData = [...groupedFieldsData[LENDER_INFORMATION]];
                break;
            default:
                baseData = [...groupedFieldsData[SELLER_INFORMATION]];
                break;
        }

        if (baseData.length) {
            setSellersStore(splitGroupedContactFieldsIntoStore(baseData));
        }

        return groupedFieldsData;
    };

    useEffect(() => {
        if (templateFormFields.length && draftFormSubmissionId) {
            const groupedFieldsData = applyLockedContactCount(generateDraftGroupFieldsData());
            setGroupedFields(handleBaseData(groupedFieldsData, intakeFormState.process));
        } else if (templateFormFields.length && prospectVersionId && formSubmissionId) {
            // Post-intake: snapshot first, then refresh identity from live contacts (see applyPostIntakeContactHydration).
            const groupedFieldsData = applyPostIntakeContactHydration(applyLockedContactCount(generatePostIntakeGroupFieldsData()));
            setGroupedFields(handleBaseData(groupedFieldsData, intakeFormState.process));
        } else if (templateFormFields.length) {
            const groupedFieldsData = applyLockedContactCount(generatePrefilledGroupFieldsData());
            setGroupedFields(handleBaseData(groupedFieldsData, intakeFormState.process));
        }
    }, [templateFormFields, contactResponsesCount, quoteFormData]);

    const handleCopyLink = async () => {
        navigator.clipboard
            .writeText(`${URL_ORIGIN}${intakeUrl}`)
            .then(() => successSnackbar(t.notifications.copiedLink))
            .catch((error) => errorSnackbar(error?.response?.data || t.notifications.couldNotCopyLink));
    };

    useEffect(() => {
        if (!contactId || !resolvedProspectId || isSubmitted || isDraftFormSubmitted) return;

        const interval = setInterval(() => {
            extendFormLockStatus({ contactId, prospectId: resolvedProspectId });
        }, FORM_LOCK_EXTENSION_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [contactId, isDraftFormSubmitted, isSubmitted, resolvedProspectId]);

    if (error) {
        return <ClientErrorPage message={error} onRetry={canRetryError ? handleRetryTemplateLoad : undefined} isRetrying={intakeFormLoading} language={language} />;
    }

    return (
        <ClientViewWrapper id={wrapperElementId} loading={intakeFormLoading}>
            {isBlockingIntakeStatus(intakeFormStatus?.Status) ? (
                <Box sx={mainWrapperStyles}>
                    <Box sx={intakeFormContainerStyles}>
                        <FormContainer>
                            {loading ? (
                                <>
                                    <Box>
                                        <Skeleton variant="rounded" style={isMobile ? { width: '100px', height: '100px' } : { width: '200px', height: '200px' }} />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                        }}
                                    >
                                        <Skeleton variant="text" sx={{ fontSize: '24px', width: '50%' }} />
                                        <Skeleton variant="text" sx={{ fontSize: '14px', width: '40%' }} />
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', gap: '10px' }}>
                                    <Box>
                                        {!textLoading ? (
                                            <LawFirmLogo lawFirmId={lawFirmId} />
                                        ) : (
                                            <Skeleton variant="rounded" style={isMobile ? { width: '100px', height: '100px' } : { width: '200px', height: '200px' }} />
                                        )}
                                    </Box>
                                    <Box>
                                        {!textLoading ? (
                                            <>
                                                <Typography variant={isMobile ? 'h3' : 'h1'}>
                                                    {intakeFormStatus?.Status && intakeFormStatus.Status in t.intakeStatus.statuses
                                                        ? t.intakeStatus.statuses[intakeFormStatus.Status as keyof typeof t.intakeStatus.statuses].title
                                                        : t.intakeStatus.fallbacks.failedTitle}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={
                                                        isMobile
                                                            ? {
                                                                  color: theme.palette.neutral.black,
                                                                  marginTop: '6px',
                                                                  marginBottom: '16px',
                                                                  fontSize: '12px',
                                                                  lineHeight: '16px',
                                                              }
                                                            : {
                                                                  color: theme.palette.neutral.black,
                                                                  marginTop: '16px',
                                                                  marginBottom: '16px',
                                                              }
                                                    }
                                                >
                                                    {intakeFormStatus?.Status && intakeFormStatus.Status in t.intakeStatus.statuses
                                                        ? t.intakeStatus.statuses[intakeFormStatus.Status as keyof typeof t.intakeStatus.statuses].message
                                                        : t.intakeStatus.fallbacks.somethingWentWrong}
                                                </Typography>
                                                {(intakeFormStatus?.Status === 'Forbidden' || intakeFormStatus?.Status === 'IntakeCompleted') && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.primary"
                                                        sx={{
                                                            marginTop: '8px',
                                                            marginBottom: '16px',
                                                            fontSize: isMobile ? '12px' : '14px',
                                                            lineHeight: isMobile ? '16px' : '20px',
                                                        }}
                                                    >
                                                        {t.formHeader.descriptions.intakeCompletedFollowUp}
                                                    </Typography>
                                                )}
                                                <LawyerInfo />
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    gap: '8px',
                                                }}
                                            >
                                                <Skeleton
                                                    variant="text"
                                                    sx={{
                                                        fontSize: '24px',
                                                        width: '250px',
                                                    }}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    sx={{
                                                        fontSize: '18px',
                                                        width: '180px',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </FormContainer>
                    </Box>
                </Box>
            ) : (
                <Box key={isGoogleTranslated ? currentStep : 'original'} sx={mainWrapperStyles}>
                    {currentStep !== 0 && !isSubmitted && <BackButton onClick={handleBack} language={language} />}
                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            marginBottom: '16px',
                            display: isSubmitted ? 'none' : 'block',
                        }}
                    >
                        <IntakeFormStepper steps={displaySteps} currentStep={currentStep} />
                    </Box>
                    <Box sx={intakeFormContainerStyles}>
                        <FormContainer>
                            <LawFirmLogo lawFirmId={lawFirmId} />
                            <Box>
                                <Typography variant={isMobile ? 'h3' : 'h1'}>
                                    {isSubmitted
                                        ? inactiveTimerReached
                                            ? formSubmissionId || inactiveSaveFailed
                                                ? t.formHeader.submitted.sessionEnded
                                                : t.formHeader.submitted.submissionSaved
                                            : isDraftFormSubmitted
                                              ? t.formHeader.submitted.draftSubmitted
                                              : t.formHeader.submitted.thankYou
                                        : getLocalizedIntakeFormTitle(intakeFormState?.process, language)}
                                </Typography>
                                {inactiveTimerReached ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.primary',
                                            marginTop: isMobile ? '6px' : '16px',
                                            fontSize: isMobile ? '12px' : 'inherit',
                                            lineHeight: isMobile ? '16px' : 'inherit',
                                            wordWrap: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {formSubmissionId || inactiveSaveFailed ? t.formHeader.descriptions.sessionEndedRefresh : t.formHeader.descriptions.submissionSavedRefresh}
                                    </Typography>
                                ) : isDraftFormSubmitted ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.primary',
                                            marginTop: isMobile ? '6px' : '16px',
                                            fontSize: isMobile ? '12px' : 'inherit',
                                            lineHeight: isMobile ? '16px' : 'inherit',
                                            wordWrap: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {t.formHeader.descriptions.draftSubmitted}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.primary',
                                            marginTop: isMobile ? '6px' : '16px',
                                            wordWrap: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: convertToHtml(isSubmitted ? intakeFormState.textAfterSubmitIntakeForClient : intakeFormState.intakeFormDescription),
                                        }}
                                    />
                                )}
                                {(isDraftFormSubmitted || inactiveTimerReached) && intakeUrl && (
                                    <div style={{ marginTop: '8px' }}>
                                        <Link href={intakeUrl} variant="body2">
                                            {t.formHeader.refreshLink}
                                        </Link>
                                        <Tooltip title={t.formHeader.copyLinkTooltip}>
                                            <IconButton onClick={handleCopyLink} size="small" sx={{ color: '#3678D7' }}>
                                                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                )}
                            </Box>
                        </FormContainer>
                    </Box>
                    <Box
                        sx={{
                            borderRadius: '8px',
                            border: `1px solid ${theme.palette.neutral.borderColor}`,
                            backgroundColor: 'neutral.white',
                            padding: '8px',
                            display: isSubmitted ? 'none' : 'flex',
                            flexDirection: 'column',
                            gap: '0px',
                            height: 'fit-content',
                        }}
                    >
                        {(Object.entries(groupedFields) || []).map(
                            ([group, fields], groupIndex) =>
                                // Conditionally render if group === 'Initial Details'
                                group === steps[currentStep] && (
                                    // generate a blank array using the wrapCount
                                    <Box key={group}>
                                        {!isGroupWrappable(group) && (
                                            <Box
                                                key={groupIndex}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                {group && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                margin: isMobile ? '8px 0px 0px 8px' : '24px 0px 0px 32px',
                                                            }}
                                                            variant={isMobile ? 'h3' : 'h2'}
                                                        >
                                                            {getStaticStepLabel(group, language)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {/* Components other than Buttons Type */}
                                                {fields
                                                    .filter((field) => {
                                                        //If 'field' is a hidden conditioning question, do not display its conditional questions
                                                        const isConditioned = field.questionSchema.isConditionalQuestion;
                                                        const conditioningField = fields.find((f) => f.questionSchema.name === field.questionSchema.conditionedBy);
                                                        const isConditioningFieldHidden = conditioningField && !conditioningField.isVisibleToUser;

                                                        if (templateType === 'QuoteIntake') {
                                                            return (field.isVisibleToUser || field.isRequiredForFullAutomation) && !(isConditioned && isConditioningFieldHidden);
                                                        } else {
                                                            return field.isVisibleToUser && !(isConditioned && isConditioningFieldHidden);
                                                        }
                                                    })
                                                    .sort((a, b) => a?.displayOrder - b?.displayOrder)
                                                    .map((field, fieldIndex) => (
                                                        <Stack key={field.templateFormFieldId}>
                                                            <FormComponent
                                                                fieldObj={field}
                                                                lawFirmId={lawFirmId as string}
                                                                language={language}
                                                                isError={field?.questionSchema?.isError}
                                                                helper={field?.questionSchema?.helper}
                                                                tabindex={field.questionSchema.order}
                                                                value={getFormFieldDisplayValue(field)}
                                                                includeFieldAutomationRequired={templateType === 'QuoteIntake'}
                                                                disabled={
                                                                    (field.name === 'email' && contactResponses?.length > 0) ||
                                                                    (questionsNameThatNeedToBeDisabled.includes(field.name) && contactResponses?.length > 1)
                                                                }
                                                                onFieldValueChange={(_name: string, value: any) => {
                                                                    handleFormDataChange();
                                                                    handleValueChange(value, groupIndex, fieldIndex, field.templateFormFieldId, _name, field);
                                                                }}
                                                            />
                                                        </Stack>
                                                    ))}
                                            </Box>
                                        )}
                                        {/* For Seller(s) information */}
                                        {isGroupWrappable(group) && (
                                            <Typography
                                                sx={{
                                                    margin: isMobile ? '8px 0px 16px 0px' : '32px 0px 32px 14px',
                                                }}
                                                variant={isMobile ? 'h3' : 'h2'}
                                            >
                                                {getStaticStepLabel(group, language)}
                                            </Typography>
                                        )}
                                        {isGroupWrappable(group) &&
                                            Array(wrapCount ? parseInt(`${wrapCount}`, 10) : 0)
                                                .fill(null)
                                                .map((_, clientsArrIndex) => (
                                                    <ExpandableWrapper
                                                        key={clientsArrIndex}
                                                        shouldWrap={true}
                                                        label={getOrdinalContactLabel(intakeFormState.process, clientsArrIndex, language, contactResponses)}
                                                        expanded={currentAccordionIndex === clientsArrIndex}
                                                        accordionExpanded={() => {
                                                            if (currentAccordionIndex === clientsArrIndex) {
                                                                setCurrentAccordionIndex(null);
                                                                return;
                                                            }
                                                            setCurrentAccordionIndex(clientsArrIndex);
                                                        }}
                                                        backgroundColor={theme.palette.primary.P_800_LIGHTER}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                            }}
                                                        >
                                                            {/* Components other than Buttons Type */}
                                                            {sellersStore.length &&
                                                                sellersStore[clientsArrIndex]
                                                                    .filter((field) => {
                                                                        //If 'field' is a hidden conditioning question, do not display its conditional questions
                                                                        const isConditioned = field.questionSchema.isConditionalQuestion;
                                                                        const conditioningField = fields.find((f) => f.questionSchema.name === field.questionSchema.conditionedBy);
                                                                        const isConditioningFieldHidden = conditioningField && !conditioningField.isVisibleToUser;

                                                                        if (templateType === 'QuoteIntake') {
                                                                            return (field.isVisibleToUser || field.isRequiredForFullAutomation) && !(isConditioned && isConditioningFieldHidden);
                                                                        } else {
                                                                            return field.isVisibleToUser && !(isConditioned && isConditioningFieldHidden);
                                                                        }
                                                                    })
                                                                    ?.sort((a, b) => a?.displayOrder - b?.displayOrder)
                                                                    .map((field, fieldIndex: number, questions) => {
                                                                        const fieldValue = getFormFieldDisplayValue(field);
                                                                        let isFieldDisabled = false;
                                                                        let typeOfIdValue;
                                                                        if (field.questionSchema.type === 'upload') {
                                                                            if (field.questionSchema.name === 'firstId') {
                                                                                const firstIdTypeField = questions?.find((q) => q.questionSchema.name === 'firstIdType');
                                                                                if (firstIdTypeField) {
                                                                                    if (isEmptyFormValue(firstIdTypeField?.questionSchema?.value)) {
                                                                                        isFieldDisabled = true;
                                                                                    } else {
                                                                                        typeOfIdValue = firstIdTypeField?.questionSchema?.value;
                                                                                    }
                                                                                }
                                                                            } else if (field.questionSchema.name === 'secondId') {
                                                                                const secondIdTypeField = questions?.find((q) => q.questionSchema.name === 'secondIdType');
                                                                                if (secondIdTypeField) {
                                                                                    if (isEmptyFormValue(secondIdTypeField?.questionSchema?.value)) {
                                                                                        isFieldDisabled = true;
                                                                                    } else {
                                                                                        typeOfIdValue = secondIdTypeField?.questionSchema?.value;
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                        return (
                                                                            <Stack key={`${clientsArrIndex}-${field.templateFormFieldId}`}>
                                                                                <FormComponent
                                                                                    fieldObj={field}
                                                                                    lawFirmId={lawFirmId as string}
                                                                                    language={language}
                                                                                    isError={field?.questionSchema?.isError}
                                                                                    helper={field?.questionSchema?.helper}
                                                                                    tabindex={field.questionSchema.order}
                                                                                    value={fieldValue}
                                                                                    typeOfIdValue={typeOfIdValue}
                                                                                    includeFieldAutomationRequired={templateType === 'QuoteIntake'}
                                                                                    disabled={
                                                                                        isFieldDisabled ||
                                                                                        (questionsNameThatNeedToBeDisabled.includes(field.name) && contactResponses?.length > 1) ||
                                                                                        (field.name === 'email' &&
                                                                                            (contactResponses?.length > 1 || (contactResponses?.length === 1 && clientsArrIndex === 0)))
                                                                                    }
                                                                                    onFieldValueChange={(_name: string, value: any) => {
                                                                                        handleValueChange(value, clientsArrIndex, fieldIndex, field.templateFormFieldId, _name, field);
                                                                                    }}
                                                                                    formGroupIndex={clientsArrIndex}
                                                                                />
                                                                            </Stack>
                                                                        );
                                                                    })}
                                                        </Box>
                                                    </ExpandableWrapper>
                                                ))}
                                        {isFinalStep && contactResponses?.length > 0 && retainerResponse?.length > 0 && <RetainerAcceptanceTable rows={retainerResponse || []} language={language} />}
                                        <ButtonSection
                                            currentStep={currentStep}
                                            numSteps={steps.length}
                                            formSubmissionId={formSubmissionId}
                                            isDraftSubmitDisabled={(contactResponses?.length > 0 && loading && isDraftFormSubmitted) || isDraftSubmitDisabled}
                                            isNotAllRetainersSigned={
                                                !formSubmissionId &&
                                                isFinalStep &&
                                                contactResponses?.length > 0 &&
                                                retainerResponse?.length > 0 &&
                                                !retainerResponse.every((item) => item.isRetainerSigned === true)
                                            }
                                            isSubmitDisabled={isDraftSubmitDisabled || isSubmitDisabled}
                                            handleDraftSubmit={handleDraftSubmit}
                                            handleBackClick={handleBack}
                                            handleNextClick={handleSubmit}
                                            handleSubmitClick={() => {
                                                setIsSubmitDisabled(true);
                                                void handleSubmit();
                                            }}
                                            language={language}
                                        />
                                    </Box>
                                ),
                        )}
                    </Box>
                </Box>
            )}
            <VerifyEmailDialog
                open={verifyOpen}
                loading={loading}
                verifyEmail={verifyEmail}
                isModalVerified={isModalVerified}
                isIntakeForm={!!formSubmissionId || (!!prospectId && !!instanceId && !draftFormSubmissionId)}
                onClose={() => setVerifyOpen(false)}
                onEmailChange={setVerifyEmail}
                language={language}
                onVerify={() => {
                    if (formSubmissionId && prospectVersionId) {
                        getPostSubmittedIntakeForm();
                    } else if (prospectId && instanceId && !draftFormSubmissionId) {
                        getContactIdForIntakeForm();
                    } else if (prospectId && draftFormSubmissionId) {
                        handleVerifyClientOnRevisit(true);
                    } else if (draftFormSubmissionId) {
                        handleVerifyClientOnRevisit(false);
                    }
                }}
            />
            {showLegalDocument && lockInfo?.globalRetainerFileId && resolvedProspectId && (
                <LegalDocumentModal
                    fileId={lockInfo.globalRetainerFileId}
                    documentDisplayName={lockInfo.documentDisplayName}
                    contactId={contactId}
                    contactEmail={verifyEmail}
                    prospectId={resolvedProspectId}
                    initialClientName={clientName}
                    onComplete={handleLegalDocumentComplete}
                    onClose={() => setShowLegalDocument(false)}
                    language={language}
                />
            )}
            {contactResponses?.length > 1 && !isSubmitted && ((!isDraftFormSubmitted && prospectId) || (formSubmissionId && prospectVersionId)) && (
                <ActivityTracker timeout={ACTIVITY_TRACKER_TIMEOUT_MS} warningTime={ACTIVITY_TRACKER_WARNING_TIME_MS} onTimeout={handleInactivityTimeout} language={language} />
            )}
        </ClientViewWrapper>
    );
}
